import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL } from "../lib/site";

const HERO_IMG = "/hero.jpg";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const scale = useTransform(scrollY, [0, 900], [1.05, 1.18]);

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-[100svh] flex-col overflow-hidden bg-lec-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={HERO_IMG}
          alt="Decorative concrete entrance on a modern Perth home"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-lec-ink via-lec-ink/55 to-lec-ink/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-lec-ink/60 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end px-5 pb-16 pt-36 md:px-10 md:pb-20">
        <h1
          data-testid="hero-heading"
          className="font-display text-[10.5vw] font-semibold uppercase leading-[0.95] tracking-tight text-lec-bone sm:text-6xl lg:text-7xl xl:text-8xl"
        >
          <MaskedLine delay={0.25}>
            Perth's <span className="text-lec-green">leading</span>
          </MaskedLine>
          <MaskedLine delay={0.38}>decorative concreters.</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-8 max-w-xl text-base text-lec-bone/85 md:text-lg"
        >
          We believe in value, functionality, and sustainability. Exposed aggregate, honed
          and liquid limestone — poured by a local family team known for great
          communication, trusted team members and professional service, for over 25 years.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-request-quote-button"
            onClick={() => scrollToId("#quote")}
            className="bg-lec-green px-8 py-4 text-sm font-bold uppercase tracking-wider text-lec-ink shadow-[6px_6px_0_rgba(247,247,247,0.9)] transition-[transform,box-shadow,background-color,color] duration-200 hover:-translate-y-0.5 hover:bg-lec-bone hover:shadow-[8px_8px_0_#49FF00]"
          >
            Request a Quote
          </button>
          <a
            href={PHONE_TEL}
            data-testid="hero-call-button"
            className="flex items-center justify-center gap-3 border border-lec-bone/40 px-8 py-4 text-sm font-bold uppercase tracking-wider text-lec-bone transition-[background-color,color,border-color] duration-200 hover:border-lec-green hover:bg-lec-ink/60 hover:text-lec-green"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            Call {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 grid grid-cols-3 divide-x divide-lec-bone/15 border-t border-lec-bone/15 pt-6"
          data-testid="hero-stats"
        >
          {[
            ["25+", "Years in the trade"],
            ["5.0", "Google rating"],
            ["100%", "Family-owned & operated"],
          ].map(([stat, label]) => (
            <div key={label} className="px-4 first:pl-0">
              <p className="font-display text-2xl font-semibold text-lec-green md:text-4xl">{stat}</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-lec-bone/60">
                {label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 right-6 z-10 hidden text-lec-bone/60 transition-colors hover:text-lec-green md:block"
        aria-label="Scroll to services"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown className="h-6 w-6" />
        </motion.span>
      </motion.button>
    </section>
  );
};
